import { customErrorCodePrefix } from '@/constants';
import { ENTITY, UserActions } from '@/enums';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import { ExtractionBatchEntity, PostExtractionBatchDto } from '@/interfaces';
import { extractionService } from '@/services/api-services';
import { CustomResponse } from '@/services/api-services/type';
import {
  AddExtractionSequences,
  ExportExtractionBatchParams,
  ExtractionParams,
  MarkCompletedExtractionStatus,
  SendExtractionToSequencingBatch,
  SequencingBatchEntity,
  UploadExtractionSequenceDataFiles,
  ValidateExtractionSequenceConcentration,
  ValidationExtractionAllowDnaExtraction,
} from '@/types';
import { CustomError, errorNotification } from '@/utils';
import { DEFAULT_PAGE_SIZE, PAGE_DEFAULT } from '@amili-v3/amili-v3-design';
import {
  MutationFunction,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { notification } from 'antd';
import { AxiosError } from 'axios';
import i18next from 'i18next';
import { chain, isEmpty, startsWith } from 'lodash';
import flatMap from 'lodash/flatMap';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { newQueryKeys, QueryKeyTypes } from './queryKeys';
import { CustomInfiniteQueryOptions, CustomMutationOptions } from './type';
import { passPageParams } from './utils';

type InfiniteQuery = CustomInfiniteQueryOptions<
  CustomResponse<ExtractionBatchEntity[]>,
  ExtractionParams
>;
const useGetInfiniteExtractionBatches = (props: {
  params: ExtractionParams;
  options?: InfiniteQuery;
}) => {
  const { params = {}, options = {} } = props;
  const { canGetExtractionBatch } = useCheckScopeCombinations(
    [[{ entity: ENTITY.EXTRACTION, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useInfiniteQuery({
    queryKey: newQueryKeys.extraction.list(passPageParams(params)),
    queryFn: extractionService.getInfinite,
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    ...options,
    enabled: (options.enabled ?? true) && canGetExtractionBatch,
  });

  const flatData = flatMap(query?.data?.pages?.map(p => p.data));

  return { ...query, flatData };
};

type DefaultQuery = Partial<
  UseQueryOptions<
    CustomResponse<ExtractionBatchEntity[]>,
    Error,
    CustomResponse<ExtractionBatchEntity[]>,
    QueryKeyTypes<ExtractionParams>
  >
>;
const useGetExtractionBatches = (props: {
  params: ExtractionParams;
  options?: DefaultQuery;
}) => {
  const { params, options = {} } = props;
  const { canGetExtractionBatch } = useCheckScopeCombinations(
    [[{ entity: ENTITY.EXTRACTION, action: UserActions.GET }]],
    { optimistic: false },
  );
  //Set up variables to update pagination
  const [page, setPage] = useState(PAGE_DEFAULT);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    const passParams = passPageParams(params);
    setPage(passParams.page);
    setPageSize(passParams.take);
  }, [params]);

  //get params value
  const queryParams = useMemo<ExtractionParams>(
    () => ({
      ...params,
      take: pageSize,
      page,
    }),
    [page, pageSize, params],
  );

  const query = useQuery({
    queryKey: newQueryKeys.extraction.list(queryParams),
    queryFn: extractionService.get,
    ...options,
    enabled: (options.enabled ?? true) && canGetExtractionBatch,
  });

  return {
    ...query,
    pageSetting: { page, setPage, pageSize, setPageSize },
  };
};

type DeleteMutation = CustomMutationOptions<void, string>;
const useDeleteExtractionBatch = (options?: DeleteMutation) => {
  const mutation = useMutation({
    mutationFn: extractionService.delete as MutationFunction<void, string>,
    mutationKey: newQueryKeys.extraction.invalidateListsKey(),
    ...options,
  });

  return mutation;
};

type QueryDetail = Partial<
  UseQueryOptions<
    ExtractionBatchEntity,
    Error,
    ExtractionBatchEntity,
    QueryKeyTypes
  >
>;
const useGetExtractionBatchDetail = (props: {
  extractionId: string;
  options?: QueryDetail;
}) => {
  const { extractionId, options = {} } = props;
  const { canGetExtractionBatch } = useCheckScopeCombinations(
    [[{ entity: ENTITY.EXTRACTION, action: UserActions.GET }]],
    { optimistic: false },
  );
  const query = useQuery({
    queryKey: newQueryKeys.extraction.detail(extractionId),
    queryFn: extractionService.getById,
    ...options,
    enabled: (options.enabled ?? true) && canGetExtractionBatch,
  });
  return query;
};

type SendToSequencingMutation = CustomMutationOptions<
  SequencingBatchEntity,
  SendExtractionToSequencingBatch
>;
const useSendExtractionToSequencingBatch = (
  options?: SendToSequencingMutation,
) => {
  const { t } = useTranslation();
  const { onSuccess, onError, ...remainingOpts } = options ?? {};
  const mutation = useMutation({
    mutationFn: extractionService.sendToSequencingBatch,
    onSuccess: (data, variables, context) => {
      notification.success({
        message: t('extraction.warningSendToSequencingBatchSuccess'),
      });
      onSuccess?.(data, variables, context);
    },
    onError: (error: AxiosError, variables, context) => {
      const data = error.response?.data as any;

      const serials = (data?.details?.serials || []) as string[];
      const content = serials.map(serial => serial.toUpperCase()).join(', ');
      errorNotification({ error, contentObj: { SERIAL: content } });
      onError?.(error, variables, context);
    },
    ...remainingOpts,
  });

  return mutation;
};

type CreateMutation = CustomMutationOptions<
  ExtractionBatchEntity,
  PostExtractionBatchDto
>;
const useCreateExtractionBatch = (options?: CreateMutation) => {
  const mutation = useMutation({
    mutationFn: extractionService.create,
    ...options,
  });

  return mutation;
};

type ExportQueryOptions = Partial<
  UseQueryOptions<
    ExtractionBatchEntity[],
    Error,
    ExtractionBatchEntity[],
    QueryKeyTypes<ExportExtractionBatchParams>
  >
>;
const useExportExtractionBatches = (props: {
  params: ExportExtractionBatchParams;
  options?: ExportQueryOptions;
}) => {
  const { params, options } = props;
  const query = useQuery({
    queryKey: newQueryKeys.extractionExport.list(params),
    queryFn: extractionService.exportExtractionBatches,
    ...options,
  });

  return query;
};

type ValidateExtractionBatchMutationOptions = CustomMutationOptions<
  ValidationExtractionAllowDnaExtraction[],
  string
>;
const useValidationBatchAllowDnaExtraction = (
  options?: ValidateExtractionBatchMutationOptions,
) => {
  const mutation = useMutation({
    mutationFn: extractionService.validateBatchAllowDnaExtraction,
    mutationKey: newQueryKeys.validateAllowDnaExtraction.invalidateListsKey(),
    ...options,
  });
  return mutation;
};

export type MarkCompletedMutationOptions = CustomMutationOptions<
  void,
  MarkCompletedExtractionStatus
>;
const useMarkCompletedExtractionStatus = (
  options?: MarkCompletedMutationOptions,
) => {
  const { t } = useTranslation();
  const { onSuccess, ...remainingOptions } = options ?? {};
  const mutation = useMutation({
    mutationFn: extractionService.markCompletedExtractionStatus,
    mutationKey: newQueryKeys.extraction.invalidateDetailsKey(),
    onSuccess: (data, variables, context) => {
      notification.success({ message: t('common.success') });
      onSuccess?.(data, variables, context);
    },
    ...remainingOptions,
  });
  return mutation;
};

export type ValidateExtractionSequenceFilesMutationOptions =
  CustomMutationOptions<
    ValidateExtractionSequenceConcentration[],
    UploadExtractionSequenceDataFiles
  >;
const useValidateExtractionSequenceDataFiles = (
  options: ValidateExtractionSequenceFilesMutationOptions = {},
) => {
  const { onError, ...remainingOptions } = options;

  const mutation = useMutation({
    mutationFn: extractionService.validateExtractionSequencesDataFiles,
    mutationKey: newQueryKeys.validateAllowDnaExtraction.invalidateListsKey(),
    meta: { showError: false },
    onError: (error: AxiosError) => {
      const data = error.response?.data as any;

      const customErrorData = chain(data)
        .pickBy((_, key) => startsWith(key, customErrorCodePrefix))
        .value();

      if (!isEmpty(customErrorData)) {
        Object.entries(customErrorData).forEach(([errorCode, errorSerials]) => {
          notification.error({
            message: i18next.t(`error.${errorCode}`),
            description: errorSerials
              .map((serial: string) => serial.toUpperCase())
              .join(', '),
          });
        });
      } else {
        errorNotification({ error: error as unknown as CustomError });
      }
    },
    ...remainingOptions,
  });
  return mutation;
};

export type CreateManyExtractionSequencesMutationOptions =
  CustomMutationOptions<void, AddExtractionSequences>;
const useCreateManyExtractionSequences = (
  options: CreateManyExtractionSequencesMutationOptions = {},
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { onSuccess, ...remainingOptions } = options;
  const mutation = useMutation({
    mutationFn: extractionService.addExtractionSequences,
    mutationKey: newQueryKeys.extraction.invalidateDetailsKey(),
    onSuccess: (data, variables, context) => {
      notification.success({ message: t('common.success') });
      onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({
        queryKey: newQueryKeys.extractionSequence.invalidateListsKey(),
      });
    },
    ...remainingOptions,
  });
  return mutation;
};

export {
  useCreateExtractionBatch,
  useCreateManyExtractionSequences,
  useDeleteExtractionBatch,
  useExportExtractionBatches,
  useGetExtractionBatchDetail,
  useGetExtractionBatches,
  useGetInfiniteExtractionBatches,
  useMarkCompletedExtractionStatus,
  useSendExtractionToSequencingBatch,
  useValidateExtractionSequenceDataFiles,
  useValidationBatchAllowDnaExtraction,
};
